import api from "@/api";
import axios from "axios";
import { createStore } from "vuex";
import { API_BASE } from "@/api";

function decoder(token) {
  try {
    const b64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(new TextDecoder("utf-8").decode(Uint8Array.from(atob(b64), c => c.charCodeAt(0))));
  } catch (e) {
    return null;
  }
}

/**
 * Copie locale des données de session (utilisateur, sites, caisse, réglages) :
 * l'application redémarre et la caisse fonctionne même sans connexion.
 */
const COPIE = "bp:session:";
function garder(cle, valeur) {
  try { localStorage.setItem(COPIE + cle, JSON.stringify(valeur)); } catch (e) { /* plein */ }
}
function reprendre(cle) {
  try { const v = localStorage.getItem(COPIE + cle); return v === null ? undefined : JSON.parse(v); } catch (e) { return undefined; }
}
const coupure = (e) => !!e && !e.response;

function expire(token) {
  const d = decoder(token);
  return d?.exp ? d.exp < Date.now() / 1000 : false;
}

export default createStore({
  state: {
    token: localStorage.getItem("token") || null,
    user: null,
    permissions: [],
    roles: [],
    // Sites (boutiques, dépôts) accessibles et site de travail choisi
    sites: [],
    siteId: Number(localStorage.getItem("siteId")) || null,
    // Session de caisse ouverte de l'utilisateur
    caisse: null,
    parametres: null,
  },

  mutations: {
    setToken(state, token) {
      state.token = token;
      localStorage.setItem("token", token);
    },
    setUser(state, user) {
      state.user = user;
      state.permissions = (user.permissions || []).map(p => String(p).toUpperCase());
      state.roles = user.roles || [];
    },
    setSites(state, sites) {
      state.sites = sites;
      const ids = sites.map(s => s.id);
      if (!ids.includes(state.siteId)) {
        const defaut = state.user?.sitesInfo?.parDefaut;
        state.siteId = ids.includes(defaut) ? defaut : (ids[0] || null);
      }
    },
    setSite(state, id) {
      state.siteId = id;
      localStorage.setItem("siteId", id);
    },
    setCaisse(state, s) {
      state.caisse = s;
      garder("caisse", s);
    },
    setParametres(state, p) {
      state.parametres = p;
      garder("parametres", p);
    },
    logout(state) {
      state.token = null;
      state.user = null;
      state.permissions = [];
      state.roles = [];
      state.sites = [];
      state.caisse = null;
      localStorage.removeItem("token");
      ["user", "caisse"].forEach(k => localStorage.removeItem(COPIE + k));
    }
  },

  actions: {
    async authenticate({ commit, dispatch }, credentials) {
      try {
        const { data } = await axios.post(`${API_BASE}/api/login`, credentials);
        if (!data.token) return { success: false };
        commit("setToken", data.token);
        const ok = await dispatch("refreshUser");
        return { success: ok };
      } catch (error) {
        const message = error.response?.data?.message || "";
        if (message && message !== "Invalid credentials.") return { success: false, message };
        return { success: false };
      }
    },

    async refreshUser({ commit, dispatch, state }) {
      if (!state.token || expire(state.token)) {
        dispatch("logout");
        return false;
      }
      try {
        const { data } = await api.getMe();
        if (data.enabled === false) {
          dispatch("logout");
          return false;
        }
        commit("setUser", data);
        garder("user", data);
        if (data.entreprise) {
          await Promise.all([dispatch("chargerSites"), dispatch("chargerCaisse"), dispatch("chargerParametres")]);
        }
        return true;
      } catch (e) {
        if (!e.response) {
          // Pas de connexion : on reprend la dernière session connue de ce compte
          const d = decoder(state.token);
          const copie = reprendre("user");
          const memeCompte = copie && d && (copie.email === d.username || copie.email === d.email || copie.id === d.id);
          if (memeCompte || d) {
            commit("setUser", memeCompte ? copie : d);
            if (memeCompte) {
              const sites = reprendre("sites");
              if (sites) commit("setSites", sites);
              const caisse = reprendre("caisse");
              if (caisse !== undefined) commit("setCaisse", caisse);
              const parametres = reprendre("parametres");
              if (parametres) commit("setParametres", parametres);
            }
            return true;
          }
        }
        dispatch("logout");
        return false;
      }
    },

    async chargerSites({ commit }) {
      try {
        const { data } = await api.get("sites");
        commit("setSites", data.data);
        garder("sites", data.data);
      } catch (e) {
        if (coupure(e) && reprendre("sites")) commit("setSites", reprendre("sites"));
        /* sans site : l'écran Paramètres invite à en créer */
      }
    },

    async chargerCaisse({ commit }) {
      try {
        const { data } = await api.get("caisse/ma-session");
        commit("setCaisse", data.session);
      } catch (e) {
        // Coupure : la caisse reste comme on l'a vue la dernière fois
        if (!coupure(e)) commit("setCaisse", null);
        else if (reprendre("caisse") !== undefined) commit("setCaisse", reprendre("caisse"));
      }
    },

    async chargerParametres({ commit }) {
      try {
        const { data } = await api.get("parametres");
        commit("setParametres", data);
      } catch (e) {
        if (coupure(e) && reprendre("parametres")) commit("setParametres", reprendre("parametres"));
        /* valeurs par défaut */
      }
    },

    logout({ commit }) {
      commit("logout");
    }
  },

  getters: {
    isAuthenticated: (s) => !!s.token && !!s.user,
    user: (s) => s.user,
    entreprise: (s) => s.user?.entreprise || null,
    isSuperAdmin: (s) => (s.roles || []).includes("ROLE_SUPER_ADMIN"),
    isPlatformAdmin: (s) => (s.roles || []).includes("ROLE_PLATFORM_ADMIN"),
    site: (s) => s.sites.find(x => x.id === s.siteId) || null,
    multiSites: (s) => s.sites.length > 1,

    hasModule: (s) => (module) => {
      const m = s.user?.entreprise?.modulesActifs;
      return !Array.isArray(m) || m.includes(module);
    },

    /** Une permission (motifs « VENTE_* » acceptés), le super admin a tout. */
    hasPermission: (s, g) => (permission) => {
      if (g.isSuperAdmin) return true;
      const p = String(permission).toUpperCase();
      if (p.endsWith("*")) {
        const debut = p.slice(0, -1);
        return s.permissions.some(x => x.startsWith(debut));
      }
      return s.permissions.includes(p);
    },

    hasAnyPermission: (s, g) => (liste = []) => liste.some(p => g.hasPermission(p)),
  }
});
